import { forwardRef } from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";

import {
  COLLAPSABLE_TRIGGER_ICON_CONTAINER_VARIANT_CLASS_MAP,
  COLLAPSABLE_TRIGGER_ICON_VARIANT_CLASS_MAP,
  COLLAPSABLE_TRIGGER_TITLE_VARIANT_CLASS_MAP,
  COLLAPSIBLE_DEFAULTS,
  type TCollapsibleTriggerElement,
  type TCollapsibleTriggerProps,
} from "@/ui/components/data-display/Collapsible/Collapsible.config";
import { Icon } from "@/ui/components/primitives/Icon";
import { cn } from "@/ui/lib/utils";
import { TextSubtitle } from "../../typography/TextSubtitle";

export const CollapsibleTrigger = forwardRef<TCollapsibleTriggerElement, TCollapsibleTriggerProps>(
  (
    {
      children,
      className,
      triggerChildren,
      triggerIcon = COLLAPSIBLE_DEFAULTS.triggerIcon,
      variant = COLLAPSIBLE_DEFAULTS.variant,
      ...props
    },
    ref,
  ) => (
    <CollapsiblePrimitive.Trigger
      data-component="CollapsibleTrigger"
      ref={ref}
      className={cn(
        "group flex w-full items-center justify-between gap-3 text-left",
        "transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <TextSubtitle
        tag="div"
        size={3}
        isResponsive={false}
        className={cn(
          "min-w-0",
          COLLAPSABLE_TRIGGER_TITLE_VARIANT_CLASS_MAP[variant],
        )}
      >
        { children }
      </TextSubtitle>
      { variant === "tertiary" ? null : triggerChildren }
      <div className={cn(
        "h-9 flex justify-center items-center",
        COLLAPSABLE_TRIGGER_ICON_CONTAINER_VARIANT_CLASS_MAP[variant],
      )}>
        <Icon
          data-component="CollapsibleTriggerIcon"
          icon={triggerIcon}
          aria-hidden="true"
          size="sm"
          className={COLLAPSABLE_TRIGGER_ICON_VARIANT_CLASS_MAP[variant]}
        />
      </div>
      { variant === "tertiary" ? triggerChildren : null }
    </CollapsiblePrimitive.Trigger>
  ),
);

CollapsibleTrigger.displayName = CollapsiblePrimitive.Trigger.displayName;
